/**
 * Confirmations humaines des actions à effet externe.
 *
 * `defineService` refuse toute action N3 sans confirmation valide. Ce domaine
 * est ce qui permet d'en obtenir une — donc ce qui rend les actions N3
 * exécutables du tout.
 *
 * Le déroulé tient en quatre temps, et c'est le même quelle que soit la
 * porte : préparer, montrer, confirmer, exécuter.
 *
 *   1. `preparer_action`  — décrit ce qui va se passer, et l'inscrit
 *   2. l'écran (ou l'agent) montre le résumé au patron
 *   3. `confirmer_action` — le patron dit oui, une fois, à cette charge-là
 *   4. `executer_action_confirmee` — rejoue exactement la charge inscrite
 *
 * Le point qui fait tenir l'ensemble : **l'appelant n'écrit ni le résumé ni
 * l'empreinte**. Les deux sortent de la définition du service, via le
 * registre ci-dessous. Un écran ne peut donc pas afficher « relance de 500 € »
 * et exécuter un envoi de 5 000 € — il ne tient pas la plume.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService, describeAction } from "../../service.js";
import type {
  ActionDescription,
  Service,
  ServiceDeps,
  ServiceInput,
} from "../../service.js";
import type { ExecutionContext } from "../../context.js";

// ─── Registre des actions confirmables ─────────────────────────────────────

/**
 * Une action N3 rendue préparable, sans exposer son type d'entrée.
 *
 * L'indirection existe pour une raison de typage : un registre hétérogène de
 * services aux entrées différentes ne se décrit pas avec un type commun sans
 * `any`. On expose donc ce que le registre a besoin d'appeler, et rien
 * d'autre.
 */
export interface ConfirmableAction {
  readonly name: string;
  /** Valide la charge puis en tire le résumé et l'empreinte. */
  describe(payload: unknown): ActionDescription;
  /** Rejoue la charge inscrite, en y adjoignant le jeton de confirmation. */
  run(
    ctx: ExecutionContext,
    payload: unknown,
    confirmationId: string,
    deps: ServiceDeps,
  ): Promise<unknown>;
}

/**
 * Inscrit un service N3 au registre.
 *
 * Le schéma n'est pas décoratif : il canonise l'entrée — valeurs par défaut
 * appliquées, champs inconnus retirés, ordre des clés fixé par le schéma.
 * Sans cette canonisation, la même intention hachée deux fois donnerait deux
 * empreintes, et l'exécution refuserait une confirmation pourtant valide.
 */
export function confirmable<I extends ServiceInput, O>(
  service: Service<I, O>,
  schema: z.ZodType<I, z.ZodTypeDef, unknown>,
): ConfirmableAction {
  return {
    name: service.name,
    describe: (payload) => describeAction(service, schema.parse(payload)),
    run: (ctx, payload, confirmationId, deps) => {
      const input = schema.parse(payload);
      return service(ctx, { ...input, confirmationId }, deps);
    },
  };
}

// ─── Entrées ───────────────────────────────────────────────────────────────

export const prepareActionInput = z.object({
  confirmationId: z.string().optional(),
  /** Le verbe à préparer. Doit être inscrit au registre. */
  action: z.string().trim().min(1),
  payload: z.unknown(),
});

export const decideActionInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid("Identifiant d'action attendu"),
});

export const listPendingActionsInput = z.object({
  confirmationId: z.string().optional(),
});

export type PrepareActionInput = z.infer<typeof prepareActionInput>;
export type DecideActionInput = z.infer<typeof decideActionInput>;
export type ListPendingActionsInput = z.infer<typeof listPendingActionsInput>;

// ─── Modèle ────────────────────────────────────────────────────────────────

export type PendingActionStatus =
  | "en_attente"
  | "confirmee"
  | "refusee"
  | "executee"
  /**
   * Confirmée, et jamais exécutée dans la fenêtre qui suit le oui.
   *
   * Ne concerne **plus** une demande qui attend : depuis le 11 septembre, une
   * demande attend aussi longtemps qu'il le faut. La péremption ne court qu'à
   * partir de la confirmation, parce que c'est là que vit le risque qu'elle
   * protège — glisser autre chose entre le oui et le geste.
   */
  | "expiree";

export interface PendingAction {
  id: string;
  action: string;
  summary: string;
  payload: unknown;
  payloadHash: string;
  status: PendingActionStatus;
  requestedBy: string | null;
  createdAt: string;
  /**
   * `null` tant que la demande attend. Posé à quinze minutes au moment de la
   * confirmation : c'est la fenêtre d'exécution, pas la durée de vie de la
   * demande.
   */
  expiresAt: string | null;
}

export interface CreatePendingActionCommand {
  action: string;
  level: ActionLevel;
  summary: string;
  payload: unknown;
  payloadHash: string;
}

export interface ConfirmationRepository {
  create(
    ctx: ExecutionContext,
    cmd: CreatePendingActionCommand,
  ): Promise<PendingAction>;
  read(ctx: ExecutionContext, id: string): Promise<PendingAction | null>;
  confirm(ctx: ExecutionContext, id: string): Promise<PendingAction>;
  reject(ctx: ExecutionContext, id: string): Promise<PendingAction>;
  listPending(ctx: ExecutionContext): Promise<PendingAction[]>;
}

// ─── Services ──────────────────────────────────────────────────────────────

export function createConfirmationServices(
  repo: ConfirmationRepository,
  registry: readonly ConfirmableAction[],
) {
  const byName = new Map(registry.map((a) => [a.name, a]));

  const lookup = (name: string): ConfirmableAction => {
    const found = byName.get(name);
    if (!found) {
      // Registre fermé : on ne prépare que ce qui a été déclaré confirmable.
      // Accepter un nom inconnu reviendrait à laisser l'appelant inventer des
      // actions, et à faire confirmer au patron des verbes qui n'existent pas.
      throw new DomainError(
        "not_found",
        `« ${name} » n'est pas une action soumise à confirmation.`,
        { action: name },
      );
    }
    return found;
  };

  const preparerAction = defineService<PrepareActionInput, PendingAction>({
    name: "preparer_action",
    // Inscrire une demande n'a aucun effet vers l'extérieur : c'est une
    // écriture interne, annulable par un simple refus. La rendre N3 aurait
    // exigé de confirmer la confirmation.
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      const described = lookup(input.action).describe(input.payload);
      return repo.create(ctx, {
        action: described.action,
        level: described.level,
        summary: described.summary,
        payload: input.payload,
        payloadHash: described.payloadHash,
      });
    },
  });

  const confirmerAction = defineService<DecideActionInput, PendingAction>({
    name: "confirmer_action",
    // N2 restreint au patron, et non N3 : confirmer n'est pas l'action, c'est
    // le geste d'autorisation. En faire une action à effet externe aurait
    // demandé une confirmation de la confirmation, indéfiniment.
    //
    // La base applique la même règle de son côté : `confirm_pending_action`
    // vérifie le rôle en SECURITY DEFINER. Deux contrôles pour une seule
    // règle, parce que le premier vit dans du code que le client exécute.
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.confirm(ctx, input.id),
  });

  const refuserAction = defineService<DecideActionInput, PendingAction>({
    name: "refuser_action",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.reject(ctx, input.id),
  });

  const listerActionsEnAttente = defineService<
    ListPendingActionsInput,
    PendingAction[]
  >({
    name: "lister_actions_en_attente",
    level: ActionLevel.Read,
    handler: (ctx) => repo.listPending(ctx),
  });

  /**
   * Exécute l'action confirmée, en rejouant la charge inscrite.
   *
   * Déclarée N2 alors qu'elle déclenche du N3 : ce n'est pas un contournement.
   * Le service appelé refait tous ses contrôles — rôle, contenu externe,
   * confirmation — et c'est lui qui consomme le jeton. Doubler ici la
   * restriction de rôle interdirait au worker de relance automatique
   * d'exécuter ce qu'un patron a pourtant confirmé.
   */
  const executerActionConfirmee = defineService<DecideActionInput, unknown>({
    name: "executer_action_confirmee",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input, deps) => {
      const pending = await repo.read(ctx, input.id);
      if (!pending) {
        throw new DomainError("not_found", "Action introuvable ou expirée.", {
          id: input.id,
        });
      }
      return lookup(pending.action).run(ctx, pending.payload, pending.id, deps);
    },
  });

  return {
    preparerAction,
    confirmerAction,
    refuserAction,
    listerActionsEnAttente,
    executerActionConfirmee,
  };
}

export type ConfirmationServices = ReturnType<typeof createConfirmationServices>;
